import React from 'react';
import { motion } from 'framer-motion';
import { LockIcon, UserCheckIcon, ZapIcon, SparklesIcon, AlertCircleIcon, CheckCircle2Icon, ArrowRightIcon, FileTextIcon } from 'lucide-react';
const TimelineNode = ({
  color,
  isLast
}: {
  color: string;
  isLast?: boolean;
}) => <div className="hidden md:flex flex-col items-center mr-6 lg:mr-8 xl:mr-12" data-id="element-471">
    <div className={`w-6 h-6 lg:w-8 lg:h-8 rounded-full border-4 border-white shadow-lg z-10 ${color}`} data-id="element-472"></div>
    {!isLast && <div className="w-0.5 h-full bg-gray-100 my-2" data-id="element-473"></div>}
  </div>;
const TierContent = ({
  title,
  subtitle,
  description,
  illustration,
  colorClass,
  isLast
}: {
  title: string;
  subtitle: string;
  description: string;
  illustration: React.ReactNode;
  colorClass: string;
  isLast?: boolean;
}) => {
  return <div className="flex pb-12 sm:pb-16 md:pb-20 lg:pb-24" data-id="element-474">
      <TimelineNode color={colorClass} isLast={isLast} data-id="element-475" />
      <motion.div initial={{
      opacity: 0,
      y: 40
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-20%'
    }} transition={{
      duration: 0.6
    }} className="flex-1 grid md:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-start" data-id="element-476">
        <div className="pt-2" data-id="element-477">
          <div className="flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-3 mb-4" data-id="element-478">
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900" data-id="element-479">
              {title}
            </h3>
            <span className={`text-xs sm:text-sm font-bold uppercase tracking-wider px-2 sm:px-3 py-1 rounded-full bg-gray-100 ${colorClass.replace('bg-', 'text-')} inline-block w-fit`} data-id="element-480">
              {subtitle}
            </span>
          </div>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 leading-relaxed" data-id="element-481">
            {description}
          </p>
        </div>
        <div className="bg-gray-50 rounded-xl sm:rounded-2xl border border-gray-100 p-4 sm:p-6 shadow-sm relative overflow-hidden" data-id="element-482">
          {/* Subtle background pattern */}
          <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] opacity-[0.5]" data-id="element-483"></div>
          <div className="relative z-10" data-id="element-484">{illustration}</div>
        </div>
      </motion.div>
    </div>;
};
// --- Illustrations ---
const AssistIllustration = () => <div className="w-full max-w-sm mx-auto bg-white rounded-lg sm:rounded-xl shadow-md border border-gray-200 overflow-hidden" data-id="element-485">
    <div className="bg-blue-50/50 px-3 sm:px-4 py-2 sm:py-3 border-b border-blue-100 flex justify-between items-center" data-id="element-486">
      <div className="flex items-center gap-2" data-id="element-487">
        <FileTextIcon size={14} className="text-blue-600 sm:w-4 sm:h-4" data-id="element-488" />
        <span className="text-xs sm:text-sm font-semibold text-gray-800" data-id="element-489">
          New Expense Report
        </span>
      </div>
      <div className="text-[9px] sm:text-[10px] font-medium text-blue-600 bg-blue-100 px-1.5 sm:px-2 py-0.5 rounded-full flex items-center gap-1" data-id="element-490">
        <SparklesIcon size={8} className="sm:w-2.5 sm:h-2.5" data-id="element-491" />
        AI Assist
      </div>
    </div>
    <div className="p-3 sm:p-5 space-y-3 sm:space-y-4" data-id="element-492">
      {/* Field 1 */}
      <div data-id="element-493">
        <label className="text-[10px] sm:text-xs font-medium text-gray-500 mb-1 block" data-id="element-494">
          Merchant
        </label>
        <div className="relative" data-id="element-495">
          <input type="text" value="Uber Technologies" readOnly className="w-full text-xs sm:text-sm border-blue-200 bg-blue-50/30 rounded-md px-2 sm:px-3 py-1.5 sm:py-2 text-gray-800" data-id="element-496" />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 text-blue-400" data-id="element-497">
            <SparklesIcon size={10} className="sm:w-3 sm:h-3" data-id="element-498" />
          </div>
        </div>
      </div>

      {/* Field 2 & 3 */}
      <div className="grid grid-cols-2 gap-2 sm:gap-3" data-id="element-499">
        <div data-id="element-500">
          <label className="text-[10px] sm:text-xs font-medium text-gray-500 mb-1 block" data-id="element-501">
            Amount
          </label>
          <div className="relative" data-id="element-502">
            <input type="text" value="$34.50" readOnly className="w-full text-xs sm:text-sm border-blue-200 bg-blue-50/30 rounded-md px-2 sm:px-3 py-1.5 sm:py-2 text-gray-800" data-id="element-503" />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 text-blue-400" data-id="element-504">
              <SparklesIcon size={10} className="sm:w-3 sm:h-3" data-id="element-505" />
            </div>
          </div>
        </div>
        <div data-id="element-506">
          <label className="text-[10px] sm:text-xs font-medium text-gray-500 mb-1 block" data-id="element-507">
            Category
          </label>
          <div className="relative" data-id="element-508">
            <input type="text" value="Transportation" readOnly className="w-full text-xs sm:text-sm border-blue-200 bg-blue-50/30 rounded-md px-2 sm:px-3 py-1.5 sm:py-2 text-gray-800" data-id="element-509" />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 text-blue-400" data-id="element-510">
              <SparklesIcon size={10} className="sm:w-3 sm:h-3" data-id="element-511" />
            </div>
          </div>
        </div>
      </div>

      {/* Locked Button */}
      <div className="pt-2" data-id="element-512">
        <button disabled className="w-full bg-gray-100 text-gray-400 text-xs sm:text-sm font-medium py-2 sm:py-2.5 rounded-lg flex items-center justify-center gap-2 cursor-not-allowed border border-gray-200 min-h-[44px]" data-id="element-513">
          <LockIcon size={12} className="sm:w-3.5 sm:h-3.5" data-id="element-514" />
          Review to Submit
        </button>
        <p className="text-[9px] sm:text-[10px] text-center text-gray-400 mt-2" data-id="element-515">
          AI cannot submit without review
        </p>
      </div>
    </div>
  </div>;
const ExecuteIllustration = () => <div className="w-full max-w-sm mx-auto bg-white rounded-lg sm:rounded-xl shadow-md border border-gray-200 overflow-hidden" data-id="element-516">
    <div className="bg-amber-50 px-3 sm:px-4 py-2 sm:py-3 border-b border-amber-100 flex items-center gap-2" data-id="element-517">
      <div className="bg-amber-100 p-1 sm:p-1.5 rounded-full text-amber-600" data-id="element-518">
        <UserCheckIcon size={14} className="sm:w-4 sm:h-4" data-id="element-519" />
      </div>
      <span className="text-xs sm:text-sm font-semibold text-gray-800" data-id="element-520">
        Approval Required
      </span>
    </div>

    <div className="p-3 sm:p-5" data-id="element-521">
      <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4" data-id="element-522">
        <div className="flex-1" data-id="element-523">
          <h4 className="text-xs sm:text-sm font-medium text-gray-900 mb-1" data-id="element-524">
            Refund Order #4821
          </h4>
          <p className="text-[10px] sm:text-xs text-gray-500 mb-2 sm:mb-3" data-id="element-525">
            Initiated by Customer Support AI
          </p>
          <div className="bg-gray-50 rounded p-2 text-[10px] sm:text-xs text-gray-700 border border-gray-100" data-id="element-526">
            <div className="flex justify-between mb-1" data-id="element-527">
              <span data-id="element-528">Amount:</span>
              <span className="font-medium" data-id="element-529">$450.00</span>
            </div>
            <div className="flex justify-between" data-id="element-530">
              <span data-id="element-531">Reason:</span>
              <span className="font-medium" data-id="element-532">Damaged Item</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 text-[9px] sm:text-[10px] text-amber-700 bg-amber-50 border border-amber-100 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg mb-3 sm:mb-4" data-id="element-533">
        <AlertCircleIcon size={10} className="sm:w-3 sm:h-3 flex-shrink-0" data-id="element-534" />
        <span data-id="element-535">Policy: Refunds &gt; $100 require manager approval</span>
      </div>

      <div className="flex gap-2 sm:gap-3" data-id="element-536">
        <button className="flex-1 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-xs sm:text-sm font-medium py-2 rounded-lg transition-colors min-h-[44px]" data-id="element-537">
          Reject
        </button>
        <button className="flex-1 bg-amber-500 hover:bg-amber-600 text-white text-xs sm:text-sm font-medium py-2 rounded-lg transition-colors shadow-sm min-h-[44px]" data-id="element-538">
          Approve
        </button>
      </div>
    </div>
  </div>;
const AutopilotIllustration = () => <div className="w-full max-w-sm mx-auto bg-white rounded-lg sm:rounded-xl shadow-md border border-gray-200 overflow-hidden" data-id="element-539">
    <div className="bg-emerald-950 text-emerald-100 px-3 sm:px-4 py-2 sm:py-3 flex justify-between items-center" data-id="element-540">
      <div className="flex items-center gap-2" data-id="element-541">
        <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-emerald-500 animate-pulse" data-id="element-542"></div>
        <span className="text-[10px] sm:text-xs font-mono font-medium uppercase tracking-wider" data-id="element-543">
          Live Execution
        </span>
      </div>
      <div className="text-[9px] sm:text-[10px] bg-emerald-900/50 px-1.5 sm:px-2 py-0.5 rounded border border-emerald-800" data-id="element-544">
        Workflow: Onboard Vendor
      </div>
    </div>

    <div className="p-3 sm:p-4 bg-gray-900 text-gray-300 font-mono text-[9px] sm:text-[10px] space-y-2 sm:space-y-3 overflow-x-auto" data-id="element-545">
      <div className="flex gap-2 sm:gap-3 items-start opacity-50" data-id="element-546">
        <span className="text-gray-500 whitespace-nowrap" data-id="element-547">[10:42:01]</span>
        <div className="flex-1 min-w-0" data-id="element-548">
          <div className="break-words" data-id="element-549">
            Trigger received: Email from new vendor
          </div>
        </div>
        <span className="text-emerald-500 whitespace-nowrap" data-id="element-550">OK</span>
      </div>

      <div className="flex gap-2 sm:gap-3 items-start" data-id="element-551">
        <span className="text-gray-500 whitespace-nowrap" data-id="element-552">[10:42:02]</span>
        <div className="flex-1 min-w-0" data-id="element-553">
          <div className="break-words" data-id="element-554">Extracting W-9 data...</div>
          <div className="text-gray-500 pl-2 border-l border-gray-700 mt-1" data-id="element-555">
            &gt; Name: Acme Corp
            <br data-id="element-556" />
            &gt; TIN: XX-XXXXXXX
          </div>
        </div>
        <span className="text-emerald-500 whitespace-nowrap" data-id="element-557">DONE</span>
      </div>

      <div className="flex gap-2 sm:gap-3 items-start" data-id="element-558">
        <span className="text-gray-500 whitespace-nowrap" data-id="element-559">[10:42:03]</span>
        <div className="flex-1 min-w-0" data-id="element-560">
          <div className="break-words" data-id="element-561">Policy Check: OFAC Sanctions List</div>
        </div>
        <span className="text-emerald-500 whitespace-nowrap" data-id="element-562">PASS</span>
      </div>

      <div className="flex gap-2 sm:gap-3 items-start" data-id="element-563">
        <span className="text-gray-500 whitespace-nowrap" data-id="element-564">[10:42:04]</span>
        <div className="flex-1 min-w-0" data-id="element-565">
          <div className="text-white font-bold break-words" data-id="element-566">
            Tool Call: create_vendor_record
          </div>
        </div>
        <span className="text-emerald-500 font-bold whitespace-nowrap" data-id="element-567">
          SUCCESS
        </span>
      </div>

      <div className="mt-2 pt-2 border-t border-gray-800 flex items-center gap-2 text-emerald-400" data-id="element-568">
        <CheckCircle2Icon size={10} className="sm:w-3 sm:h-3 flex-shrink-0" data-id="element-569" />
        <span className="break-words" data-id="element-570">Workflow completed successfully</span>
      </div>
    </div>
  </div>;
const FreedomDial = () => {
  return <section className="w-full bg-white py-16 sm:py-24 md:py-32 lg:py-48" id="autonomy" data-id="element-571">
      <div className="container mx-auto px-4 sm:px-6 md:px-8" data-id="element-572">
        <div className="max-w-4xl mx-auto mb-12 sm:mb-16 md:mb-20 lg:mb-24" data-id="element-573">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 tracking-tight leading-tight" data-id="element-574">
            You control how much freedom AI gets
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl" data-id="element-575">
            Not every workflow should be autonomous. FluidWeb lets you define
            the operating envelope — per workflow, per role, per step.
          </motion.p>
        </div>

        <div className="max-w-5xl mx-auto" data-id="element-576">
          <TierContent title="Assist" subtitle="Locked" description="AI suggests and fills, but cannot execute actions. Everything stays within your schema and validations." illustration={<AssistIllustration data-id="element-578" />} colorClass="bg-blue-500" data-id="element-577" />
          <TierContent title="Execute" subtitle="Supervised" description="AI can run actions, but requires approvals for sensitive steps — money movement, data changes, external calls." illustration={<ExecuteIllustration data-id="element-580" />} colorClass="bg-amber-500" data-id="element-579" />
          <TierContent title="Autopilot" subtitle="Bounded" description="AI executes end-to-end within a strict policy box — permissions, budgets, rate limits, tool allowlists — with full audit logs." illustration={<AutopilotIllustration data-id="element-582" />} colorClass="bg-emerald-500" isLast={true} data-id="element-581" />
        </div>

        <div className="mt-8 sm:mt-10 md:mt-12 text-center" data-id="element-583">
          <p className="text-xs sm:text-sm font-medium text-gray-400 uppercase tracking-widest px-4" data-id="element-584">
            Start locked. Expand autonomy only where the workflow proves safe.
          </p>
        </div>
      </div>
    </section>;
};
export default FreedomDial;